import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'uxd-design-system-components';
import PropTypes from 'prop-types';
import SendToDelegatorData from '~types/api/SendToDelegatorData';
import GoToFinancialFoundationsButtonProps from '~types/components/GoToFinancialFoundationsButtonProps';
import getConType from '../../utils/getConType/getConType';
import getText from '../../assets/locale/locale';
import useQuery from '../../utils/useQuery/useQuery';
import addDataLayerTrigger from '../../utils/addDataLayerTrigger/addDataLayerTrigger';
import LocaleContext from '../../contexts/LocaleContext';
import { handleAVIError, sendToDelegator } from '../../api/index';

const GoToFinancialFoundationsButton = ({
  setNotificationContent,
  setNotificationOpen,
  sessionId,
  resultsJSON,
  faInfo,
  planningTopics,
  investmentInvolvementCode,
}: GoToFinancialFoundationsButtonProps) => {
  const locId = useContext(LocaleContext);
  const urlQuery = useQuery();
  const faNo = urlQuery.get('faNo') || faInfo.faNo;
  const conId = urlQuery.get('conId');
  const utmSource = urlQuery.get('utm_source');

  const [isSaveResultsLoading, setIsSaveResultsLoading] = useState(false);
  const [
    isSaveResultsButtonDisabled,
    setIsSaveResultsButtonDisabled,
  ] = useState(false);

  useEffect(() => {
    // Add when utm_source is another app that called MP
    window.addEventListener('beforeunload', () => {
      window.opener.postMessage(
        { functionNa: 'refreshDashboard' },
        window.FINANCIAL_FOUNDATIONS_ORIGIN
      );
    });
  }, []);

  async function saveResults() {
    setIsSaveResultsLoading(true);
    addDataLayerTrigger('email-fa', { faId: faNo });
    const params = {
      locale: locId,
      notifyFaInd: 'N',
      storeToDbInd: 'Y',
      archiveInd: 'Y',
      sysId: 'PRP',
      appName: 'MyPriorities',
      conType: getConType(utmSource, null),
      utmSource,
    };

    const data: SendToDelegatorData = {
      faNo,
      contactNo: conId,
      contactName: null,
      branchNo: faInfo.branchNo,
      results: resultsJSON,
      sessionId,
      utmSource,
      userComments: null,
      coldContactData: null,
      planningTopics,
      investmentInvolvementCode,
    };

    const responseType = 'json';

    sendToDelegator(params, data, responseType)
      .then(() => {
        setIsSaveResultsLoading(false);
        setIsSaveResultsButtonDisabled(true);
        addDataLayerTrigger('confirmation-visible', {});
        window.close();
      })
      .catch(error => {
        if (error.response && error.response.status === 403) {
          handleAVIError(error, locId);
        }
        setIsSaveResultsLoading(false);
        setNotificationContent({
          message: getText(locId, 'saveResultsErrorMessage', 'resultsPage'),
          type: 'error',
        });
        setNotificationOpen(true);
        /* eslint-disable no-console */
        console.error(error);
      });
  }

  return (
    <>
      {isSaveResultsButtonDisabled ? (
        <Button
          label={getText(locId, 'successButton', 'resultsPage')}
          styleVariant="reversed-secondary"
          disabled
          attributes={{
            value: '',
            id: 'save-results-button',
            'data-testid': 'save-results-button',
          }}
          onClick={() => {}}
        />
      ) : (
        <Button
          loading={isSaveResultsLoading}
          icon="message-send"
          label={getText(locId, 'saveResultsFF', 'resultsPage')}
          onClick={() => saveResults()}
          attributes={{
            value: '',
            id: 'save-results-button',
            'data-testid': 'save-results-button',
          }}
        />
      )}
    </>
  );
};

GoToFinancialFoundationsButton.propTypes = {
  setNotificationContent: PropTypes.func.isRequired,
  setNotificationOpen: PropTypes.func.isRequired,
  sessionId: PropTypes.string.isRequired,
  resultsJSON: PropTypes.objectOf(PropTypes.any).isRequired,
  faInfo: PropTypes.objectOf(PropTypes.string).isRequired,
  planningTopics: PropTypes.arrayOf(PropTypes.string),
  investmentInvolvementCode: PropTypes.string,
};

export default GoToFinancialFoundationsButton;
